import type { GameStateManager } from '../game/GameState';
import type { GameData, ResearcherData } from '../game/types';

export class ScientistSelectionModal {
  private selectedScientists: Set<ResearcherData> = new Set();
  private availableScientists: ResearcherData[] = [];
  private onConfirmCallback: ((scientists: ResearcherData[]) => void) | null = null;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData
  ) {
    this.setup();
  }

  private setup(): void {
    // No backdrop click handling - modal cannot be dismissed
  }

  private selectRandomScientists(): ResearcherData[] {
    const allScientists = [...this.gameData.researchers];
    // Shuffle using Fisher-Yates algorithm
    for (let i = allScientists.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allScientists[i], allScientists[j]] = [allScientists[j], allScientists[i]];
    }
    // Return first 4
    return allScientists.slice(0, 4);
  }

  private shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  private toggleSelection(scientist: ResearcherData): void {
    if (this.selectedScientists.has(scientist)) {
      this.selectedScientists.delete(scientist);
    } else {
      if (this.selectedScientists.size < 2) {
        this.selectedScientists.add(scientist);
      }
    }
    this.render();
  }

  private confirm(): void {
    if (this.selectedScientists.size === 2 && this.onConfirmCallback) {
      const selected = Array.from(this.selectedScientists);
      this.onConfirmCallback(selected);
      this.hide();
    }
  }

  show(onConfirm: (scientists: ResearcherData[]) => void): void {
    this.onConfirmCallback = onConfirm;
    this.selectedScientists.clear();
    const selectedScientists = this.selectRandomScientists();
    // Shuffle the order of scientists for display
    this.availableScientists = this.shuffleArray(selectedScientists);
    this.render();
    this.container.classList.remove('hidden');
    this.gameState.setPausedByModal(true);
    
    // Focus the modal content
    const modalContent = this.container.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.focus();
    }
  }

  hide(): void {
    this.container.classList.add('hidden');
    this.gameState.setPausedByModal(false);
    this.selectedScientists.clear();
  }

  isOpen(): boolean {
    return !this.container.classList.contains('hidden');
  }

  private render(): void {
    const canConfirm = this.selectedScientists.size === 2;

    const scientistCards = this.availableScientists.map(scientist => {
      const isSelected = this.selectedScientists.has(scientist);
      const specialRules = scientist.special_rules.map(rule => {
        const tooltipKey = `ability.${rule.toLowerCase()}`;
        return `<span class="special-ability" data-tooltip-key="${tooltipKey}">${rule}</span>`;
      }).join(', ');
      
      return `
        <div class="scientist-card ${isSelected ? 'selected' : ''}" data-scientist-name="${scientist.name}">
          <div class="scientist-header">
            <h3>${scientist.name}</h3>
            <div class="scientist-sanity">Sanity: ${scientist.sanity}</div>
          </div>
          <div class="scientist-description">${scientist.description}</div>
          <div class="scientist-special-rules">
            <strong>Special Rules:</strong> ${specialRules}
          </div>
        </div>
      `;
    }).join('');

    this.container.innerHTML = `
      <div class="modal-backdrop modal-backdrop-no-dismiss"></div>
      <div class="modal-content scientist-selection-modal" tabindex="0">
        <div class="modal-header">
          <h2>Select Your Team</h2>
        </div>
        <div class="modal-body">
          <div class="selection-counter">Selected: ${this.selectedScientists.size}/2</div>
          <div class="scientist-list">
            ${scientistCards}
          </div>
        </div>
        <div class="modal-footer">
          <button class="button-confirm ${!canConfirm ? 'disabled' : ''}" 
                  ${!canConfirm ? 'disabled' : ''}
                  data-action="confirm">Confirm Selection</button>
        </div>
      </div>
    `;

    // Add click handlers to scientist cards
    this.availableScientists.forEach(scientist => {
      const card = this.container.querySelector(`[data-scientist-name="${scientist.name}"]`) as HTMLElement;
      if (card) {
        card.addEventListener('click', () => {
          this.toggleSelection(scientist);
        });
      }
    });

    // Attach confirm button event listener
    const confirmButton = this.container.querySelector('[data-action="confirm"]') as HTMLElement;
    if (confirmButton) {
      confirmButton.addEventListener('click', () => {
        if (canConfirm) {
          this.confirm();
        }
      });
    }
  }
}
